// src/components/forms/formEventEngine.ts

import { EventContext, FormEvent } from "../../types/interfaces";


type HandlerFunc = (context: EventContext) => void;

// 🔗 Global registry
const eventHandlers: Record<string, Record<FormEvent, HandlerFunc[]>> = {};
const formWideHandlers: Record<FormEvent, HandlerFunc[]> = {};

// ===============
// API
// ===============

export function addHandler(
    targetIdOrForm: string,
    event: FormEvent,
    handler: HandlerFunc
) {
    if (targetIdOrForm === "*") {
        if (!formWideHandlers[event]) formWideHandlers[event] = [];
        formWideHandlers[event].push(handler);
    } else {
        if (!eventHandlers[targetIdOrForm]) eventHandlers[targetIdOrForm] = {};
        if (!eventHandlers[targetIdOrForm][event]) eventHandlers[targetIdOrForm][event] = [];
        eventHandlers[targetIdOrForm][event].push(handler);
    }
}

export function removeHandler(
    targetIdOrForm: string,
    event: FormEvent,
    handler?: HandlerFunc
) {
    const targetHandlers =
        targetIdOrForm === "*"
            ? formWideHandlers[event]
            : eventHandlers[targetIdOrForm]?.[event];

    if (!targetHandlers) return;

    if (handler) {
        const index = targetHandlers.indexOf(handler);
        if (index >= 0) targetHandlers.splice(index, 1);
    } else {
        if (targetIdOrForm === "*") {
            delete formWideHandlers[event];
        } else {
            delete eventHandlers[targetIdOrForm][event];
        }
    }
}

export function clearAllHandlers() {
    Object.keys(eventHandlers).forEach((field) => delete eventHandlers[field]);
    Object.keys(formWideHandlers).forEach((evt) => delete formWideHandlers[evt]);
}

// =====================
// Trigger / Emit
// =====================

export function triggerUserEvent(fieldOrSystemId: string, event: string, context: EventContext = {}) {
    const handlers = eventHandlers[fieldOrSystemId]?.[event];
    if (!handlers || handlers.length === 0) return;

    // Automatically include triggering fieldId in context if not already present
    const enrichedContext: EventContext = {
        ...context,
        fieldId: context.fieldId ?? fieldOrSystemId
    };

    for (const handler of handlers) {
        try {
            handler(enrichedContext);
        } catch (err) {
            console.warn(`Handler error for ${fieldOrSystemId} -> ${event}:`, err);
        }
    }
}

// =====================
// Optional Util
// =====================

export function getRegisteredEvents() {
    return {
        fields: Object.keys(eventHandlers),
        formWide: Object.keys(formWideHandlers),
    };
}
